/* eslint-disable */
import { createRef, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  fade,
  IconButton,
  makeStyles,
} from '@material-ui/core';
import { useQuery } from 'react-fetching-library';
import cx from 'classnames';
import ContentWrapper from 'components/Wrappers/ContentWrapper';
import useMetadataUpdate from 'hooks/useMetadataUpdate';
import { ASSESSMENT_STATUSES } from 'components/Assessments/constants';
import smoothscroll from 'smoothscroll-polyfill';
import { getLocalizedDate } from '../../helpers/localize';
import ReportNavigation from './widgets/Navigation';
import ProvidersModal from './widgets/ProvidersModal';
import EmptyPageLoader from '../Shared/EmptyPageLoader';
import {
  INFO_PANE_PAGES,
  openInfoPane,
  toggleIsInfoPaneExpanded,
  enqueueSnackbar,
  SNACKBAR_VARIANTS,
  selectActivePatientInfo,
  selectActivePatientUuid,
} from '../../redux_store';
import { useDispatch, useSelector } from 'react-redux';
import HelpModals from './widgets/HelpModals/HelpModals';
import ContentHeader from './widgets/ContentHeader';
import { getCount } from './helpers';
import ReportContent from './widgets/ReportContent';
import { SURVEYS_HAVE_HELP_INFO } from './constants';
import PatientInfoBlock from '../PatientDashboard/widgets/PatientInfoBlock';
import LoadingErrorWidget from '../Shared/LoadingErrorWidget';

// --------- adding smooth scrolling for click nav link ---------
smoothscroll.polyfill();

const useStyles = makeStyles((theme) => ({
  actions: {
    height: '100%',
  },
  patientName: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px',
    marginLeft: -12,
    borderRadius: 10,
    background: 'transparent',
    transition: '.3s',
    fontStyle: 'normal',

    '&:hover': {
      transition: '.3s',
      color: '#0073b0',
    },
  },
  infoIcon: {
    width: 16,
    marginLeft: 4,
    color: '#868686',
  },
  driver: {
    borderTop: '1px solid rgba(0, 0, 0, 0.13)',
  },
  helpIcon: {
    padding: 8,
    border: `1px solid ${theme.palette.error.light}`,
    marginLeft: 5,
    marginBottom: 'auto',
    width: 20,
    height: 20,
    cursor: 'pointer',
    borderColor: theme.colors.lightBlue,
    fontSize: 12,
    color: theme.colors.lightBlue,
  },
  notPrintingArea: {
    '@media print': {
      display: 'none',
    },
    overflowY: 'hidden',
    padding: theme.spacing(1.25, 0, 0, 3),
  },
  underTitleText: {
    fontSize: 15,
    fontStyle: 'italic',
    color: fade(theme.colors.blue2, 0.5),
    marginTop: theme.spacing(1),
  },
  actionsStyle: {
    [theme.breakpoints.between(0, 1315)]: {
      paddingTop: 0,
    },
  },
  headerContent: {
    display: 'flex',
    position: 'sticky',
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: 'white',
    zIndex: 3,
    boxShadow: '3px 9px 15px -19px rgba(0, 0, 0, 0.97)',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}));

const ReportView = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const topRef = useRef();
  const { en: { notifications, titles, infoPane } } = useSelector((state) => state.i18n);
  const { currentPage } = useSelector((state) => state.infoPane);
  const [openProvidersModal, toggleProvidersModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingComparing, setIsLoadingComparing] = useState(true);
  const [showHelpPopup, setShowHelpPopup] = useState(false);
  const [blockTitles, setBlockTitles] = useState([]);
  const [blockTitlesComparing, setBlockTitlesComparing] = useState([]);
  const [currentBlockId, setCurrentBlockId] = useState();
  const [refs, setRefs] = useState({});
  const [surveysInfo, setSurveyInfo] = useState();
  const [surveysComparisonInfo, setSurveyComparisonInfo] = useState();
  const [counterOpenTabs, setCounterOpenTabs] = useState(0);
  const [isNavigationPinned, setIsNavigationPinned] = useState(false);
  const [alignment, setAlignment] = useState('left');
  const [isOpenDefaultTab, setIsOpenDefaultTab] = useState(true);
  const {
    patientUuid: paramsPatientUuid,
    accountUuid: paramAccountUuid,
    assessmentUuid,
    status: paramStatus,
  } = useParams();
  const params = useParams();
  const {
    metadata: { currentAccount },
    isDistributionSponsor,
  } = useSelector((state) => state.user);
  const [status, setStatus] = useState(paramStatus);
  const patientUuid = useSelector(selectActivePatientUuid);
  const patientInfo = useSelector(selectActivePatientInfo);
  const updateCurrentBlockId = (id, hasDelayed = false) => {
    setTimeout(() => {
      setCurrentBlockId(id);
    }, hasDelayed ? 600 : 0);
  };

  const openAllBlock = () => {
    const newRefs = { ...refs };
    Object.keys(refs).forEach((refKeys) => {
      newRefs[refKeys].open = true;
      setCounterOpenTabs(Object.keys(refs).length);
    });
    isOpenDefaultTabSelected(newRefs);
    setRefs(newRefs);
  };

  const closeAllBlock = () => {
    const newRefs = { ...refs };
    Object.keys(refs).forEach((refKeys) => {
      newRefs[refKeys].open = false;
      setCounterOpenTabs(0);
    });
    isOpenDefaultTabSelected(newRefs);
    setRefs(newRefs);
  };

  const isOpenDefaultTabSelected = (newRefs) => {
    let newIsOpenDefaultTab = !(surveysComparisonInfo || surveysInfo).surveyItems.find(item =>
      item.expandedByDefault !== newRefs[item.title].open,
    );
    if (newIsOpenDefaultTab !== isOpenDefaultTab) {
      setIsOpenDefaultTab(newIsOpenDefaultTab);
    }
  };

  const setOpenBlock = (title) => {
    const newRefs = { ...refs };
    setCounterOpenTabs(
      newRefs[title].open ? counterOpenTabs - 1 : counterOpenTabs + 1,
    );
    newRefs[title].open = !newRefs[title].open;
    isOpenDefaultTabSelected(newRefs);
    setRefs(newRefs);
  };

  const setOpenBlockByDefault = () => {
    let counterExpandedByDefaultTabs = 0;
    const refs = [...(surveysComparisonInfo?.surveyItems ?? []), ...(surveysInfo?.surveyItems ?? [])].reduce(
      (refsObj, { title, expandedByDefault }) => {
        if (expandedByDefault) counterExpandedByDefaultTabs += 1;
        refsObj[title] = {
          open: !!expandedByDefault,
          ref: createRef(),
        };
        return refsObj;
      },
      {},
    );
    setIsOpenDefaultTab(true);
    setCounterOpenTabs(counterExpandedByDefaultTabs);
    setRefs(refs);
  };

  const updateReport = async () => {
    getSurveyInfo();
    setStatus(ASSESSMENT_STATUSES.completed);
    dispatch(
      enqueueSnackbar({
        message: notifications.assessmentAccepted,
        options: {
          variant: SNACKBAR_VARIANTS.success,
        },
      }),
    );
  };

  const {
    query: querySurveys,
    loading: loadingSurveys,
    error: getDataErrorReport,
  } = useMetadataUpdate(useQuery, [
    {
      method: 'GET',
      endpoint: `/accounts/${paramAccountUuid || currentAccount.uuid}/surveys/${assessmentUuid}/report`,
    },
    false,
  ]);

  const {
    query: querySurveysComparison,
    loading: loadingSurveysComparison,
    error: getDataErrorComparisonReport,
  } = useMetadataUpdate(useQuery, [
    {
      method: 'GET',
      endpoint: `/accounts/${paramAccountUuid || currentAccount.uuid}/surveys/${assessmentUuid}/survey-comparison`,
    },
    false,
  ]);


  useEffect(() => {
    if (!isLoading && surveysInfo) {
      let newBlockTitles = [];
      surveysInfo.surveyItems.forEach(({
                                         title,
                                         hideAutoGeneratedReportContent,
                                         reportContent,
                                       }) => {
        if (!(!!hideAutoGeneratedReportContent === true && !!reportContent)) {
          newBlockTitles = [...newBlockTitles, title];
        }
      });
      setBlockTitles(newBlockTitles);
      setCurrentBlockId(newBlockTitles[0]);
    }
  }, [isLoading]);

  const getSurveyInfo = () => {
    querySurveys().then(({ error, payload }) => {
      if (!error) {
        const newSurveyInfo = {
          ...payload,
          termsAcceptedTimestamp: getLocalizedDate(
            payload.termsAcceptedTimestamp,
          ),
        };
        newSurveyInfo.surveyItems = newSurveyInfo.surveyItems.map((props) => {
          const colors = getCount(props.surveyItems);
          return { ...props, colors, title: props.title.replace('.', '') };
        });
        setSurveyInfo(newSurveyInfo);
        setIsLoading(false);
      }
    });
  };

  const getSurveyComparisonInfo = () => {
    setIsLoadingComparing(true);
    querySurveysComparison().then(({ error, payload }) => {
      if (!error) {
        const newSurveyInfo = {
          ...payload.basicComparison,
          termsAcceptedTimestamp: getLocalizedDate(
            payload.basicComparison.termsAcceptedTimestamp,
          ),
        };
        newSurveyInfo.surveyItems = newSurveyInfo.surveyItems.map((props) => {
          const colors = getCount(props.surveyItems);
          return { ...props, colors, title: props.title.replace('.', '') };
        });
        if (payload.customComparison && payload.customComparison.length) {
          const customComparisonInfo = {
            'surveyItems': payload.customComparison.map((item) => ({
              ...item,
              'itemType': 'table',
            })),
            'hasValue': true,
            'expandedByDefault': false,
            'uuid': 'customComparison',
            'itemType': 'group',
            'title': 'Patient progress',
            'hideAutoGeneratedReportContent': false,
          };
          newSurveyInfo.surveyItems = [customComparisonInfo, ...newSurveyInfo.surveyItems];
        }

        setSurveyComparisonInfo(newSurveyInfo);
        let newBlockTitles = [];
        newSurveyInfo.surveyItems.forEach(({
                                             title,
                                             hideAutoGeneratedReportContent,
                                             reportContent,
                                           }) => {
          if (!(!!hideAutoGeneratedReportContent === true && !!reportContent)) {
            newBlockTitles = [...newBlockTitles, title];
          }
        });
        setBlockTitlesComparing(newBlockTitles);
        setAlignment('justify');
      }
      setIsLoadingComparing(false);
    });
  };

  const getData = () => {
    if (paramsPatientUuid && !loadingSurveys) {
      getSurveyInfo();
      getSurveyComparisonInfo();
    }
  };

  useEffect(() => {
    getData();
  }, [paramsPatientUuid]);

  useEffect(() => {
    if (!isLoadingComparing && !isLoading) {
      setOpenBlockByDefault();
    }
  }, [isLoadingComparing, isLoading]);

  if (isLoading || loadingSurveysComparison || loadingSurveys) {
    return (getDataErrorReport && !loadingSurveys)
      ? <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <LoadingErrorWidget onReload={getData} />
      </div>
      : <EmptyPageLoader />;
  }

  return (
    <>
      {
        isDistributionSponsor &&
        <ProvidersModal
          updateReport={updateReport}
          openProvidersModal={openProvidersModal}
          toggleProvidersModal={toggleProvidersModal}
        />
      }
      <ContentWrapper
        maxWidth={'100%'}
        containerStyle={{ position: 'relative' }}
        titleStyle={{ overflow: 'hidden' }}
        className={classes.notPrintingArea}
        underTitleText={<div style={{ display: 'flex', alignItems: 'center' }}>
          <PatientInfoBlock onSecondStyleClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const isPaneAlreadyOpenForThisInfo = ((currentPage?.currentPageContext?.patientUUID ?? currentPage?.currentPageContext?.uuid) === patientUuid);
            if (currentPage?.currentPageName !== INFO_PANE_PAGES.patient_page || !isPaneAlreadyOpenForThisInfo) {
              dispatch(openInfoPane({
                currentPageName: INFO_PANE_PAGES.patient_page,
                currentPageContext: {
                  ...patientInfo,
                  uuid: paramsPatientUuid,
                  accountUUID: paramAccountUuid || currentAccount.uuid,
                },
              }));
            } else {
              dispatch(toggleIsInfoPaneExpanded(true));
            }
          }}
          />
          <div style={{ margin: '0 12px' }}>{'•'}</div>
          <div>
            {`${titles.testDate}: ${surveysInfo.termsAcceptedTimestamp}`}
          </div>
        </div>}
        underTitleTextStyle={classes.underTitleText}
        actionsStyle={cx(classes.actionsStyle, classes.actions)}
        titleText={`${surveysInfo.localizedTitle} ${titles.report}`}
        additionTitleButton={
          SURVEYS_HAVE_HELP_INFO.includes(surveysInfo.localizedTitle) ?
            <IconButton
              className={classes.helpIcon}
              onClick={() => {
                setShowHelpPopup(true);
              }}
            >{'?'}</IconButton>
            : null
        }
      >
        <div className={classes.driver} ref={topRef} />
        <div className={classes.headerContent}>
          <ContentHeader
            surveysInfo={surveysInfo}
            patientInfo={patientInfo}
            setOpenBlockByDefault={setOpenBlockByDefault}
            isOpenDefaultTab={isOpenDefaultTab}
            counterOpenTabs={counterOpenTabs}
            toggleProvidersModal={toggleProvidersModal}
            status={status}
            isRightTabDisabled={!surveysComparisonInfo}
            updateReport={updateReport}
            openAllBlock={openAllBlock}
            closeAllBlock={closeAllBlock}
            alignment={alignment}
            setAlignment={setAlignment}
          />
          <ReportNavigation
            topRef={topRef}
            refs={refs}
            setRefs={setRefs}
            currentBlockId={currentBlockId}
            setCurrentBlockId={updateCurrentBlockId}
            blockTitles={alignment === 'left' ? blockTitles : blockTitlesComparing}
            setOpenBlock={setOpenBlock}
            isOpenDefaultTabSelected={isOpenDefaultTabSelected}
            isNavigationPinned={isNavigationPinned}
            setIsNavigationPinned={setIsNavigationPinned}
          />
        </div>

        {
          (alignment !== 'left' && getDataErrorComparisonReport && !loadingSurveysComparison) ?
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}><LoadingErrorWidget onReload={getData} /></div> :
            <ReportContent
              surveysInfo={alignment === 'left' ? surveysInfo : surveysComparisonInfo}
              isComparing={alignment !== 'left'}
              refs={refs}
              updateCurrentBlockId={updateCurrentBlockId}
              currentBlockId={currentBlockId}
              setOpenBlock={setOpenBlock}
              isNavigationPinned={isNavigationPinned}
            />
        }

        <HelpModals
          isLoading={isLoading}
          localizedTitle={surveysInfo.localizedTitle}
          setShowHelpPopup={setShowHelpPopup}
          showHelpPopup={showHelpPopup}
        />
      </ContentWrapper>
    </>
  );
};

export default ReportView;
